import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import { HoverLift } from "@/components/motion";
import { useFeature } from "@/lib/useFeature";
import {
  useListRenewals,
  useUpdateRenewal,
  useStartRenewal,
  getListRenewalsQueryKey,
  type Renewal,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { format } from "date-fns";
import { Filter, Calendar, User, FileText, RefreshCw, ArrowRight, LayoutGrid, Rows3 } from "lucide-react";

type Stage = "not_started" | "negotiation" | "verbal_agreement" | "drafted" | "signed" | "closed";

const STAGES: { value: Stage; label: string; color: string; accent: string }[] = [
  {
    value: "not_started",
    label: "Not Started",
    color: "status-inactive",
    accent: "bg-muted-foreground/40",
  },
  {
    value: "negotiation",
    label: "Negotiation",
    color: "bg-[hsl(220_65%_45%/0.12)] text-[hsl(220_65%_35%)] border border-[hsl(220_65%_45%/0.25)]",
    accent: "bg-[hsl(220_65%_45%)]",
  },
  {
    value: "verbal_agreement",
    label: "Verbal Agreement",
    color: "bg-[hsl(265_55%_50%/0.12)] text-[hsl(265_55%_40%)] border border-[hsl(265_55%_50%/0.25)]",
    accent: "bg-[hsl(265_55%_50%)]",
  },
  {
    value: "drafted",
    label: "Drafted",
    color: "status-pending",
    accent: "bg-gold",
  },
  {
    value: "signed",
    label: "Signed",
    color: "status-active",
    accent: "bg-field-green",
  },
  {
    value: "closed",
    label: "Closed",
    color: "status-inactive",
    accent: "bg-border",
  },
];

export default function Renewals() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";
  const draftFeature = useFeature("contracts.draft");

  const [sportFilter, setSportFilter] = useState<string>("all");
  const [windowFilter, setWindowFilter] = useState<string>("all");
  const [view, setView] = useState<"board" | "table">("board");
  const [editing, setEditing] = useState<Renewal | null>(null);
  const [editForm, setEditForm] = useState({ assigneeUserId: "", targetDate: "", notes: "" });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: number; clerkUserId: string | null; email: string; name: string | null; status: string }>>([]);

  useEffect(() => {
    let cancel = false;
    fetch("/api/team/members")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancel) setTeamMembers(Array.isArray(data) ? data : []); })
      .catch(() => { /* ignore */ });
    return () => { cancel = true; };
  }, []);

  const assignableMembers = useMemo(
    () => teamMembers.filter((m) => m.clerkUserId),
    [teamMembers],
  );

  const params: Record<string, string> = {};
  if (windowFilter !== "all") params.window = windowFilter;
  const { data: renewals, isLoading } = useListRenewals(params);
  const updateRenewal = useUpdateRenewal();
  const startRenewal = useStartRenewal();

  const sports = useMemo(() => {
    const s = new Set<string>();
    renewals?.forEach((r) => r.sport && s.add(r.sport));
    return Array.from(s).sort();
  }, [renewals]);

  const filtered = useMemo(() => {
    if (!renewals) return [];
    return renewals.filter((r) => sportFilter === "all" || r.sport === sportFilter);
  }, [renewals, sportFilter]);

  const grouped = useMemo(() => {
    const map: Record<Stage, Renewal[]> = {
      not_started: [], negotiation: [], verbal_agreement: [], drafted: [], signed: [], closed: [],
    };
    filtered.forEach((r) => { map[r.stage as Stage].push(r); });
    return map;
  }, [filtered]);

  const persistedCount = (renewals?.filter((r) => r.id !== null).length) ?? 0;
  const expiringSoon = (renewals?.filter((r) => r.daysUntilExpiry <= 90 && r.stage !== "closed" && r.stage !== "signed").length) ?? 0;

  const handleStartOrMove = (r: Renewal, newStage: Stage) => {
    if (!isAdmin) return;
    const stageLabel = STAGES.find((s) => s.value === newStage)?.label;
    const onErr = (err: unknown) => handleApiError(toast, err, { fallbackTitle: "Could not update renewal stage" });
    if (r.id == null) {
      startRenewal.mutate(
        { data: { contractId: r.contractId, stage: newStage } },
        {
          onSuccess: () => {
            toast({ title: "Renewal started", description: `${r.coachName} moved to ${stageLabel}` });
            queryClient.invalidateQueries({ queryKey: getListRenewalsQueryKey(params) });
          },
          onError: onErr,
        }
      );
    } else {
      updateRenewal.mutate(
        { id: r.id, data: { stage: newStage } },
        {
          onSuccess: () => {
            toast({ title: "Stage updated", description: `${r.coachName} → ${stageLabel}` });
            queryClient.invalidateQueries({ queryKey: getListRenewalsQueryKey(params) });
          },
          onError: onErr,
        }
      );
    }
  };

  const openEdit = (r: Renewal) => {
    setEditing(r);
    setEditForm({
      assigneeUserId: r.assigneeUserId ?? "",
      targetDate: r.targetDate ?? "",
      notes: r.notes ?? "",
    });
  };

  const saveEdit = () => {
    if (!editing || editing.id == null) return;
    const renewalId = editing.id;
    updateRenewal.mutate(
      {
        id: renewalId,
        data: {
          assigneeUserId: editForm.assigneeUserId || null,
          targetDate: editForm.targetDate || null,
          notes: editForm.notes || null,
        },
      },
      {
        onSuccess: () => {
          toast({ title: "Renewal updated" });
          queryClient.invalidateQueries({ queryKey: getListRenewalsQueryKey(params) });
          setEditing(null);
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not save renewal" }),
      }
    );
  };

  const promoteToContract = (r: Renewal) => {
    const search = new URLSearchParams({
      coachId: String(r.coachId),
      renewalId: r.id ? String(r.id) : "",
      annualSalary: String(r.annualSalary),
      fromContractId: String(r.contractId),
    });
    window.location.href = `${import.meta.env.BASE_URL}contracts/new?${search.toString()}`;
  };

  const cardKey = (r: Renewal) => `${r.id ?? "new"}-${r.contractId}`;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Renewals Pipeline</h1>
          <p>Track and progress contract renewals from negotiation through execution.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">
        <HoverLift>
          <Card className="card-lift kpi-card-blue h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active Renewals</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-4 w-4 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value">{isLoading ? "—" : persistedCount}</div>
              <p className="text-xs text-muted-foreground">In progress</p>
            </CardContent>
          </Card>
        </HoverLift>
        <HoverLift>
          <Card className="card-lift kpi-card-gold h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Expiring ≤ 90 days</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="h-4 w-4 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value text-orange-600">{isLoading ? "—" : expiringSoon}</div>
              <p className="text-xs text-muted-foreground">Need attention</p>
            </CardContent>
          </Card>
        </HoverLift>
        <HoverLift>
          <Card className="card-lift h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Tracked</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <FileText className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value">{isLoading ? "—" : filtered.length}</div>
              <p className="text-xs text-muted-foreground">All cards on board</p>
            </CardContent>
          </Card>
        </HoverLift>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {sports.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={windowFilter} onValueChange={setWindowFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Within 12 months</SelectItem>
                <SelectItem value="30">Expiring ≤ 30 days</SelectItem>
                <SelectItem value="60">Expiring ≤ 60 days</SelectItem>
                <SelectItem value="90">Expiring ≤ 90 days</SelectItem>
                <SelectItem value="180">Expiring ≤ 180 days</SelectItem>
              </SelectContent>
            </Select>
            <div className="hidden sm:flex ml-auto items-center rounded-md border bg-muted/30 p-0.5">
              <button
                type="button"
                onClick={() => setView("board")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm transition-colors ${view === "board" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-pressed={view === "board"}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Board
              </button>
              <button
                type="button"
                onClick={() => setView("table")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm transition-colors ${view === "table" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                aria-pressed={view === "table"}
              >
                <Rows3 className="h-3.5 w-3.5" /> Table
              </button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {isLoading ? (
        view === "table" ? (
          <Card>
            <CardContent className="p-0">
              <div className="rounded-xl border overflow-hidden">
                <Table className="table-refined">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Coach</TableHead>
                      <TableHead>Sport</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Contract Ends</TableHead>
                      <TableHead>Days Left</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Target Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-[140px] skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-[110px] rounded-full skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-[100px] skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-[120px] skeleton-shimmer" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-[70px] skeleton-shimmer" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-8 w-[60px] ml-auto skeleton-shimmer" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STAGES.map((s) => <Skeleton key={s.value} className="h-[400px] skeleton-shimmer rounded-xl" />)}
          </div>
        )
      ) : view === "table" ? (
        <Card>
          <CardContent className="p-0">
            <div className="rounded-xl border overflow-hidden">
              <Table className="min-w-[900px] table-refined">
                <TableHeader>
                  <TableRow>
                    <TableHead>Coach</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Contract Ends</TableHead>
                    <TableHead>Days Left</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead>Target Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="p-0">
                        <EmptyState
                          icon={RefreshCw}
                          title="No renewals match your filters"
                          description="Try adjusting your sport or expiry-window filter."
                          variant="compact"
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r) => {
                      const stageMeta = STAGES.find((s) => s.value === r.stage);
                      const daysClass =
                        r.daysUntilExpiry < 0
                          ? "text-signal-red font-semibold"
                          : r.daysUntilExpiry <= 30
                          ? "text-signal-red font-medium"
                          : r.daysUntilExpiry <= 90
                          ? "text-[hsl(var(--gold))] font-medium"
                          : "text-foreground";
                      return (
                        <TableRow key={cardKey(r)}>
                          <TableCell className="font-semibold">
                            <Link href={`/coaches/${r.coachId}`} className="hover:underline">
                              {r.coachName}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{r.sport}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${stageMeta?.color ?? ""}`}>
                              {stageMeta?.label ?? r.stage}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(r.contractEndDate), "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className={`kpi-value text-sm ${daysClass}`}>
                            {r.daysUntilExpiry < 0
                              ? `${Math.abs(r.daysUntilExpiry)} overdue`
                              : `${r.daysUntilExpiry} days`}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.assigneeName ? (
                              <span className="inline-flex items-center gap-1">
                                <User className="h-3 w-3 text-muted-foreground" />
                                {r.assigneeName}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.targetDate ? format(new Date(r.targetDate), "MMM d, yyyy") : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="ghost" asChild>
                                <Link href={`/contracts/${r.contractId}`}>View</Link>
                              </Button>
                              {isAdmin && r.id !== null && (
                                <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                              )}
                              {isAdmin && r.stage === "signed" && draftFeature.enabled && (
                                <Button size="sm" variant="outline" onClick={() => promoteToContract(r)}>
                                  Promote <ArrowRight className="h-3 w-3 ml-1" />
                                </Button>
                              )}
                              {isAdmin && r.stage === "signed" && !draftFeature.enabled && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href="/settings#plans" title="Requires Draft plan">
                                    Promote <ArrowRight className="h-3 w-3 ml-1" />
                                  </a>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {STAGES.map((stage) => (
            <div
              key={stage.value}
              data-testid={`renewal-column-${stage.value}`}
              className={`relative overflow-hidden rounded-xl border bg-muted/20 p-2 pt-3 min-h-[120px] md:min-h-[300px] transition-shadow ${draggingId ? "ring-1 ring-dashed ring-primary/40" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const key = e.dataTransfer.getData("text/plain");
                const r = filtered.find((x) => cardKey(x) === key);
                if (r && r.stage !== stage.value) handleStartOrMove(r, stage.value);
                setDraggingId(null);
              }}
            >
              <div className={`absolute top-0 left-0 right-0 h-[3px] ${stage.accent}`} />
              <div className="flex items-center justify-between mb-3 px-1">
                <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${stage.color}`}>
                  {stage.label}
                </span>
                <span className="text-xs font-medium text-muted-foreground tabular-nums">{grouped[stage.value].length}</span>
              </div>

              <div className="space-y-2">
                {grouped[stage.value as Stage].map((r) => (
                  <Card
                    key={cardKey(r)}
                    draggable={isAdmin}
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", cardKey(r));
                      setDraggingId(cardKey(r));
                    }}
                    onDragEnd={() => setDraggingId(null)}
                    className={`card-lift cursor-${isAdmin ? "grab" : "default"} border-border/70`}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <Link href={`/coaches/${r.coachId}`} className="font-medium text-sm hover:underline">
                          {r.coachName}
                        </Link>
                        <Badge variant="outline" className="text-xs shrink-0">{r.sport}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Ends {format(new Date(r.contractEndDate), "MMM d, yyyy")}
                        </div>
                        <div className={r.daysUntilExpiry <= 30 ? "text-red-600 font-medium" : r.daysUntilExpiry <= 90 ? "text-orange-600" : ""}>
                          {r.daysUntilExpiry < 0 ? `${Math.abs(r.daysUntilExpiry)} days overdue` : `${r.daysUntilExpiry} days left`}
                        </div>
                        {r.assigneeName && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />{r.assigneeName}
                          </div>
                        )}
                        {r.targetDate && (
                          <div>Target: {format(new Date(r.targetDate), "MMM d")}</div>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          <Link href={`/contracts/${r.contractId}`}>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2">View</Button>
                          </Link>
                          {r.id !== null && (
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => openEdit(r)}>
                              Edit
                            </Button>
                          )}
                          {r.stage === "signed" && draftFeature.enabled && (
                            <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => promoteToContract(r)}>
                              Promote <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          )}
                          {r.stage === "signed" && !draftFeature.enabled && (
                            <Button size="sm" variant="outline" className="h-6 text-xs px-2" asChild>
                              <a href="/settings#plans" title="Requires Draft plan">
                                Promote <ArrowRight className="h-3 w-3 ml-1" />
                              </a>
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {grouped[stage.value].length === 0 && (
                  <div className="text-xs text-muted-foreground italic text-center py-6">No items</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Renewal — {editing?.coachName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Assignee</Label>
              <Select
                value={editForm.assigneeUserId === "" ? "__unassigned" : editForm.assigneeUserId}
                onValueChange={(value) =>
                  setEditForm((f) => ({ ...f, assigneeUserId: value === "__unassigned" ? "" : value }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a teammate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned">Unassigned</SelectItem>
                  {assignableMembers.map((m) => (
                    <SelectItem key={m.clerkUserId!} value={m.clerkUserId!}>
                      {m.name ? `${m.name} (${m.email})` : m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assignableMembers.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No active teammates yet. Invite teammates from Settings.
                </p>
              )}
            </div>
            <div>
              <Label>Target Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={editForm.targetDate}
                onChange={(e) => setEditForm((f) => ({ ...f, targetDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                className="mt-1"
                placeholder="Negotiation notes, blockers, etc."
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={updateRenewal.isPending}>
              {updateRenewal.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
